import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../components/ui/dialog";
import AuditTable, { type AuditTableColumn } from "../../audit/AuditTable";

interface SummaryLine {
  label: string;
  value: string;
}

interface SummaryTableModalProperties<Row> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Already-translated header title. */
  title: string;
  /** sr-only dialog description. */
  description: string;
  /** sr-only table caption. */
  caption: string;
  /** Bold label/value pairs rendered above the table (e.g. a total). */
  summaryLines?: SummaryLine[];
  columns: Array<AuditTableColumn<Row>>;
  rows: Row[];
}

// Read-only popup shell: header bar, summary lines, breakdown table and a Cancel footer
function SummaryTableModal<Row>({
  open,
  onOpenChange,
  title,
  description,
  caption,
  summaryLines = [],
  columns,
  rows,
}: SummaryTableModalProperties<Row>) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-(--spacing-0) p-(--spacing-0) overflow-hidden bg-white sm:max-w-lg"
      >
        <div className="bg-primary text-primary-foreground px-(--spacing-4) py-(--spacing-2) flex items-center justify-between">
          <DialogTitle className="text-primary-foreground text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold)">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">{description}</DialogDescription>
          <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-(--spacing-0-5) transition-colors">
            <X className="size-4.5" aria-hidden="true" />
            <span className="sr-only">{t("summaryModal.close")}</span>
          </DialogClose>
        </div>

        <div className="gap-(--spacing-3) p-(--spacing-6) flex flex-col">
          {summaryLines.map((line) => (
            <div
              key={line.label}
              className="gap-(--spacing-12) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold) flex"
            >
              <span>{line.label}</span>
              <span>{line.value}</span>
            </div>
          ))}

          <AuditTable caption={caption} columns={columns} rows={rows} />
        </div>

        <DialogFooter className="px-(--spacing-4) py-(--spacing-2) justify-center border-t sm:justify-center">
          <DialogClose asChild>
            <Button type="button" size="xs">
              {t("summaryModal.cancel")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SummaryTableModal;
